import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { CatalogCardBody, CatalogCardFooter } from '@/shared/components/CatalogCardLayout'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { toneSoftClass } from './tone'

const discountPillClass = cn(
  'inline-flex items-center rounded-pill border px-2.5 py-0.5 text-xs font-semibold shadow-sm',
  toneSoftClass.accent,
)

export function OfferCard({
  title,
  subtitle,
  media,
  badges,
  discount,
  price,
  priceBesideAction = false,
  details,
  actionLabel,
  actionAriaLabel,
  actionTo,
  actionSearch,
  headingLevel = 3,
  className,
}: {
  title: string
  subtitle?: string
  media?: ReactNode
  badges?: ReactNode
  /** First-class discount emphasis (V3-P2). Pill overlaid on the media, or above the title when there is no media. */
  discount?: ReactNode
  price?: ReactNode
  /** Render `price` on the CTA row (right of the button) instead of as a block above it. */
  priceBesideAction?: boolean
  details?: ReactNode
  actionLabel: string
  /** Explicit accessible name for the CTA (must contain `actionLabel` to satisfy WCAG 2.5.3).
   *  Lets a short visible label like "Détails" carry the item context for screen readers. */
  actionAriaLabel?: string
  actionTo: string
  /** Optional TanStack Router `search` passed to media / title / CTA links. */
  actionSearch?: Record<string, unknown>
  /** Niveau Hn du titre selon le contexte d'imbrication (défaut h3, sous une section h2). */
  headingLevel?: 2 | 3 | 4
  className?: string
}) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4'
  return (
    <article className={cn('flex h-full flex-col rounded-md border border-border bg-card shadow-sm', className)}>
      {media ? (
        <div className="relative overflow-hidden rounded-t-md bg-muted">
          <Link to={actionTo} search={actionSearch} tabIndex={-1} aria-hidden className="block focus:outline-none">
            {media}
          </Link>
          {discount ? <span className={cn('absolute right-2 top-2', discountPillClass)}>{discount}</span> : null}
        </div>
      ) : null}
      <CatalogCardBody>
        {discount && !media ? <span className={cn('self-start', discountPillClass)}>{discount}</span> : null}
        {badges ? <div className="flex flex-wrap gap-2">{badges}</div> : null}
        <Heading className="font-display text-lg font-semibold leading-snug text-foreground">
          <Link
            to={actionTo}
            search={actionSearch}
            className="text-inherit no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {title}
          </Link>
        </Heading>
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
        {details ? <div className="text-sm text-muted-foreground">{details}</div> : null}
        {price && !priceBesideAction ? <div className="min-w-0">{price}</div> : null}
        <CatalogCardFooter inlineAlign={priceBesideAction}>
          <Button variant="default" size="sm" asChild>
            <Link to={actionTo} search={actionSearch} aria-label={actionAriaLabel}>
              {actionLabel}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
          {price && priceBesideAction ? <div className="min-w-0">{price}</div> : null}
        </CatalogCardFooter>
      </CatalogCardBody>
    </article>
  )
}
